/**
 * Detecta automaticamente a URL da API baseada no ambiente
 * - Em desenvolvimento local: usa localhost
 * - Em dispositivo móvel: detecta IP da máquina ou usa variável de ambiente
 *
 * Retorna a URL BASE do backend (sem /api/v1), pois as chamadas já usam paths
 * como /api/v1/auth/login. Evita duplicação (ex: /api/v1/api/v1/auth/login).
 */
function stripApiV1(url: string): string {
  return url.replace(/\/api\/v1\/?$/, '') || url;
}

export function getApiBaseUrl(): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const isLocalhostHost = hostname === 'localhost' || hostname === '127.0.0.1';

  // Determinar se deve usar HTTPS ou HTTP
  // Em produção (não localhost), sempre usar HTTPS se a página estiver em HTTPS
  const useHttps = protocol === 'https:' || !isLocalhostHost;
  const baseProtocol = useHttps ? 'https' : 'http';

  // 0. Produção (Cloudflare, domínio público): URL do backend injetada no build.
  // Tem prioridade sobre REACT_APP_API_URL quando esta aponta para localhost — caso típico:
  // build com Dockerfile/env local (localhost:8081) deployado para Pages; sem isso o código
  // ignorava o env e caía no fallback http://localhost:8081 no browser do utilizador.
  if (!isLocalhostHost && process.env.REACT_APP_API_URL_PRODUCTION) {
    const baseUrl = stripApiV1(process.env.REACT_APP_API_URL_PRODUCTION);
    console.log('[getApiUrl] Usando REACT_APP_API_URL_PRODUCTION:', baseUrl);
    return baseUrl;
  }

  // 1. Verificar variável de ambiente (mais confiável)
  // Se o app está sendo acessado por IP/dominio, evitar usar localhost da build
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    const isLocalhostEnv = envUrl.includes('localhost') || envUrl.includes('127.0.0.1');

    if (!isLocalhostHost && isLocalhostEnv) {
      console.warn('[getApiUrl] Ignorando REACT_APP_API_URL localhost em acesso remoto');
    } else {
      const baseUrl = stripApiV1(envUrl);
      console.log('[getApiUrl] Usando base URL:', baseUrl);
      return baseUrl;
    }
  }
  
  // 2. Verificar se há IP salvo no localStorage (configurado pelo usuário)
  // IMPORTANTE: Se estamos acessando pelo IP, priorizar o IP salvo sobre detecção automática
  const savedIP = localStorage.getItem('backend_ip');
  if (savedIP) {
    // Validar que o IP não contém caracteres maliciosos
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(savedIP)) {
      console.error('[getApiUrl] IP salvo inválido, removendo do localStorage');
      localStorage.removeItem('backend_ip');
    } else {
      console.log('[getApiUrl] Usando IP salvo do localStorage:', savedIP);
      console.log('[getApiUrl] Hostname atual:', hostname);
      // Para IPs salvos, usar HTTP apenas em localhost, HTTPS em produção
      const savedProtocol = (hostname === 'localhost' || hostname === '127.0.0.1') ? 'http' : baseProtocol;
      return `${savedProtocol}://${savedIP}:8081`;
    }
  }
  
  // 3. Se estamos em localhost, usar localhost com HTTP
  if (isLocalhostHost) {
    console.log('[getApiUrl] Localhost detectado, usando localhost:8081');
    return 'http://localhost:8081';
  }
  
  // 4. Se estamos acessando por IP, usar o mesmo IP para o backend
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipRegex.test(hostname)) {
    console.log('[getApiUrl] IP detectado na URL, usando para backend:', hostname);
    console.log('[getApiUrl] Dica: Salve este IP em /setup-backend-ip para persistir');
    // Para IPs detectados, usar protocolo baseado no contexto
    return `${baseProtocol}://${hostname}:8081`;
  }
  
  // 5. Se estamos acessando por domínio local, tentar descobrir IP
  if (hostname.includes('finance-control.local') || hostname.includes('.local')) {
    // Para domínios .local, tentar extrair IP da URL se possível
    // Ou usar o hostname (pode funcionar se mDNS estiver configurado)
    console.warn('[getApiUrl] Domínio local detectado. Configure o IP em /setup-backend-ip');
    // Tentar usar o hostname (pode funcionar com mDNS)
    return `${baseProtocol}://${hostname}:8081`;
  }
  
  // 6. Fallback: em domínio público, NUNCA assumir localhost no cliente (quebra health/API)
  if (!isLocalhostHost) {
    console.error(
      '[getApiUrl] Sem URL de API para este host. Defina REACT_APP_API_URL_PRODUCTION (e/ou REACT_APP_API_URL) no build com o URL do backend (ex.: Cloud Run). Hostname:',
      hostname
    );
    // Evita requisições para localhost no dispositivo do utilizador
    return '';
  }

  console.warn('[getApiUrl] Não foi possível determinar URL da API, usando localhost. Configure em /setup-backend-ip');
  return 'http://localhost:8081';
}

